import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";

// Layout
import RootLayout from "./components/layout/RootLayout";

// MUI Layout
import { CustomThemeProvider } from "./styles/ThemeContext";
import { AuthProvider } from "./hooks/useAuth";

// Pages
import Home from "./routes/Home";
import Login from "./routes/Login";
import Callback from "./routes/Callback";
import SearchResults from "./routes/SearchResults";
import About from "./routes/About";
import ResourceHub from "./routes/ResourceHub";
import Contact from "./routes/Contact";
import Donate from "./routes/Donate";

// Admin Pages
import RequireAuth from "./utils/RequireAuth";
import AdminPanel from "./routes/admin/AdminPanel";
import AdminAddItems from "./routes/admin/AdminAddItems";
import AdminEditItems from "./routes/admin/AdminEditItems";
import AdminEditSingleItem from "./routes/admin/AdminEditSingleItem";
import AdminAddTrades from "./routes/admin/AdminAddTrades";
import AdminEditTrades from "./routes/admin/AdminEditTrades";
import AdminMigrateData from "./routes/admin/AdminMigrateData";
import AdminGeneratePetpage from "./routes/admin/AdminGeneratePetpage";
import AdminMarkRerelease from "./routes/admin/AdminMarkRerelease";
import AdminMarkNotes from "./routes/admin/AdminMarkNotes";
import AdminDeleteTrade from "./routes/admin/AdminDeleteTrade";
import AdminDashboard from "./routes/admin/AdminDashboard";

// Articles & Guides
import BeginnerGuide from "./routes/guides/BeginnerGuide";
import BFGBC from "./routes/articles/BFGBC";
import BuyingNC from "./routes/guides/BuyingNC";
import HTFItemGuide from "./routes/guides/HTFItemGuide";
import LebronBotGuide from "./routes/guides/LebronBotGuide";
import NeosignaturesPage from "./routes/articles/NeosignaturesPage";
import PrivacyPolicy from "./routes/PrivacyPolicy";
import QuestionsPage from "./routes/articles/QuestionsPage";
import ShuffleRules from "./routes/guides/ShuffleRules";
import SiteMap from "./routes/SiteMap";
import TokenTradingGuide from "./routes/guides/TokenTradingGuide";
import Terms from "./routes/Terms";
import UserscriptPage from "./routes/articles/UserscriptPage";
import ValuesPage from "./routes/articles/ValuesPage";
import NCQuiz2026 from "./routes/articles/NCQuiz2026";

// Updates
import ProjectUpdates from "./routes/updates/ProjectUpdates";
import September2025 from "./routes/updates/September2025";
import November2025 from "./routes/updates/November2025";

// Error Components
import ErrorPage from "./components/ErrorPage";
import NotFoundPage from "./components/NotFoundPage";

import DropRateSimulatorPage from "./routes/articles/DropRateSimulatorPage";
import TokenSearch from "./routes/TokenSearch";
import AdminManageOptions from "./routes/admin/AdminManageOptions";
import AdminViewTrades from "./routes/admin/AdminViewTrades";
import AdminPasteTrades from "./routes/admin/AdminPasteTrades";
import SystemExplanation from "./routes/articles/SystemExplanation";
import AdminUpdateUserscript from "./routes/admin/AdminUpdateUserscript";
import AdminEvents from "./routes/admin/AdminEvents";
import AdminNeoboardsSourceParser from "./routes/admin/AdminNeoboardsSourceParser";
import AdminViewEvents from "./routes/admin/AdminViewEvents";
import June2026 from "./routes/updates/June2026";
import MiscNCKnowledge from "./routes/articles/MiscNCKnowledge";
import MME from "./routes/articles/MME";
import October2026 from "./routes/updates/October2026";

// Build the router
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Home /> },
      { path: "about-us", element: <About /> },
      { path: "admin-login", element: <Login /> },
      { path: "callback", element: <Callback /> },
      { path: "site-map", element: <SiteMap /> },
      { path: "resource-hub", element: <ResourceHub /> },
      { path: "contact", element: <Contact /> },
      { path: "support", element: <Donate /> },
      { path: "privacy-policy", element: <PrivacyPolicy /> },
      { path: "terms-of-service", element: <Terms /> },

      // Search
      { path: "search", element: <Home />, index: false }, // redirect to Home
      { path: "token-search", element: <TokenSearch /> },
      { path: "search/:item", element: <SearchResults /> },

      // Updates
      { path: "updates", element: <ProjectUpdates /> },
      { path: "updates/sept-2025", element: <September2025 /> },
      { path: "updates/nov-2025", element: <November2025 /> },
      { path: "updates/june-2026", element: <June2026 /> },
      { path: "updates/oct-2026", element: <October2026 /> },

      // Articles
      { path: "userscript", element: <UserscriptPage /> },
      { path: "faq", element: <QuestionsPage /> },
      { path: "articles/bfgbc", element: <BFGBC /> },
      { path: "articles/values", element: <ValuesPage /> },
      { path: "articles/neosignatures", element: <NeosignaturesPage /> },
      { path: "articles/drop-rate-simulator", element: <DropRateSimulatorPage /> },
      { path: "articles/how-a-value-guide-works", element: <SystemExplanation /> },
      { path: "articles/misc-nc-knowledge", element: <MiscNCKnowledge /> },
      { path: "articles/mme", element: <MME /> },
      { path: "articles/anniversary-nc-quiz", element: <NCQuiz2026 /> },

      // Guides
      { path: "guides/beginner-nc-guide", element: <BeginnerGuide /> },
      { path: "guides/nc-shuffles", element: <ShuffleRules /> },
      { path: "guides/how-to-lebron", element: <LebronBotGuide /> },
      { path: "guides/buying-nc", element: <BuyingNC /> },
      { path: "guides/hunting-htf-items", element: <HTFItemGuide /> },
      { path: "guides/token-trading", element: <TokenTradingGuide /> },

      // Admin (all protected)
      { path: "admin", element: <RequireAuth><AdminPanel /></RequireAuth> },
      { path: "admin/addItems", element: <RequireAuth><AdminAddItems /></RequireAuth> },
      { path: "admin/editItems", element: <RequireAuth><AdminEditItems /></RequireAuth> },
      { path: "admin/editSingleItem", element: <RequireAuth><AdminEditSingleItem /></RequireAuth> },
      { path: "admin/addTrades", element: <RequireAuth><AdminAddTrades /></RequireAuth> },
      { path: "admin/editTrades", element: <RequireAuth><AdminEditTrades /></RequireAuth> },
      { path: "admin/markRR", element: <RequireAuth><AdminMarkRerelease /></RequireAuth> },
      { path: "admin/marknotes", element: <RequireAuth><AdminMarkNotes /></RequireAuth> },
      { path: "admin/makePetpage", element: <RequireAuth><AdminGeneratePetpage /></RequireAuth> },
      { path: "admin/migrateData", element: <RequireAuth><AdminMigrateData /></RequireAuth> },
      { path: "admin/dashboard", element: <RequireAuth><AdminDashboard /></RequireAuth> },
      { path: "admin/deleteTrades", element: <RequireAuth><AdminDeleteTrade /></RequireAuth> },
      { path: "admin/manageOptions", element: <RequireAuth><AdminManageOptions /></RequireAuth> },
      { path: "admin/viewTrades", element: <RequireAuth><AdminViewTrades /></RequireAuth> },
      { path: "admin/pasteTrades", element: <RequireAuth><AdminPasteTrades /></RequireAuth> },
      { path: "admin/updateUserscript", element: <RequireAuth><AdminUpdateUserscript /></RequireAuth> },
      { path: "admin/neoboardsSourceParser", element: <RequireAuth><AdminNeoboardsSourceParser /></RequireAuth> },
      { path: "admin/manageEvents", element: <RequireAuth><AdminEvents /></RequireAuth> },
      { path: "admin/viewEvents", element: <RequireAuth><AdminViewEvents /></RequireAuth> },

      // Catch-all route - MUST be last
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

const App = () => (
  <CustomThemeProvider>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </CustomThemeProvider>
);

export default App;
